import { logActivity } from '../utils/activityLog';
import React, { useState, useEffect } from 'react';
import { Crown, Star, GripVertical, RefreshCw, Building2, Layers, Users, ChevronDown, ChevronRight } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import api from '../services/api';
import { toast } from 'sonner';
import { getCurrentUser, getHeadRoles, getLeaderRoles, getRoleLabels } from '../utils/roles';

const ROLE_ORDER   = { owner: 0, admin: 1, crm_manager: 2 };

const AVATAR_GRADIENTS = [
  ['#7c3aed','#4f46e5'], ['#0891b2','#0e7490'], ['#059669','#047857'],
  ['#d97706','#b45309'], ['#db2777','#be185d'], ['#7c3aed','#6d28d9'],
  ['#2563eb','#1d4ed8'], ['#dc2626','#b91c1c'],
];

function avatarGradient(name = '') {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffff;
  return AVATAR_GRADIENTS[h % AVATAR_GRADIENTS.length];
}

function Avatar({ name = '?', size = 32, ring = false, ringColor = '#7c3aed' }) {
  const [c1, c2] = avatarGradient(name);
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: `linear-gradient(135deg, ${c1}, ${c2})`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: Math.round(size * 0.38), fontWeight: 700, color: '#fff',
      boxShadow: ring
        ? `0 0 0 2px ${ringColor}, 0 0 0 4px rgba(0,0,0,0.3)`
        : `0 1px 3px rgba(0,0,0,0.25)`,
    }}>
      {name.charAt(0).toUpperCase()}
    </div>
  );
}

function StatPill({ count, label, color }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600,
      background: color + '22', color, border: `1px solid ${color}44`,
    }}>
      {count} {label}
    </span>
  );
}

// ── Tree branch connector ─────────────────────────────────────────────────────
function Branch({ items, color, renderItem, gap = 8, ml = 20, pl = 14 }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ marginLeft: ml, paddingLeft: pl, borderLeft: `2px solid ${color}`, paddingTop: 6, paddingBottom: 2 }}>
      {items.map((item, i) => (
        <div key={item.id ?? i} style={{ position: 'relative', marginBottom: i < items.length - 1 ? gap : 0 }}>
          <div style={{ position: 'absolute', left: -pl, top: 16, width: pl, height: 2, background: color, borderRadius: 1 }} />
          {renderItem(item, i)}
        </div>
      ))}
    </div>
  );
}

export default function Hierarchy() {
  const { theme } = useTheme();
  const { t } = useLanguage();
  const isDark = theme === 'dark';
  const me = getCurrentUser();
  const isOwner = me.role === 'owner';
  const HEAD_ROLES = new Set(getHeadRoles());
  const LEADER_ROLES = new Set(getLeaderRoles());
  const ROLE_LABEL = getRoleLabels();

  const [offices,      setOffices]      = useState([]);
  const [desks,        setDesks]        = useState([]);
  const [teams,        setTeams]        = useState([]);
  const [users,        setUsers]        = useState([]);
  const [clientCounts, setClientCounts] = useState({}); // { user_id: count }
  const [loading,      setLoading]      = useState(true);
  const [dragging,     setDragging]     = useState(null);
  const [dropTarget,   setDropTarget]   = useState(null);
  const [editingRole,  setEditingRole]  = useState(null);
  const [collapsed,    setCollapsed]    = useState(new Set());

  const toggle = (id) => setCollapsed(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const isCol = (id) => collapsed.has(id);
  const Caret = ({ id, size = 11, color }) => isCol(id)
    ? <ChevronRight size={size} color={color} />
    : <ChevronDown  size={size} color={color} />;

  // ── Role permissions ──────────────────────────────────────────────────────
  const CHANGEABLE_BY = {
    owner:       ['admin','crm_manager','head_of_sales','head_of_ret','sales_team','ret_team','sales_agent','ret_agent'],
    admin:       ['crm_manager','head_of_sales','head_of_ret','sales_team','ret_team','sales_agent','ret_agent'],
    crm_manager: ['head_of_sales','head_of_ret','sales_team','ret_team','sales_agent','ret_agent'],
  };
  const ASSIGNABLE_ROLES = {
    owner: [
      { value: 'admin',         label: 'Admin' },
      { value: 'crm_manager',   label: 'CRM Manager' },
      { value: 'head_of_sales', label: 'Head of Sales' },
      { value: 'head_of_ret',   label: 'Head of Ret' },
      { value: 'sales_team',    label: 'TL Sales' },
      { value: 'ret_team',      label: 'TL Ret' },
      { value: 'sales_agent',   label: 'Sales Agent' },
      { value: 'ret_agent',     label: 'Ret Agent' },
    ],
    admin: [
      { value: 'crm_manager',   label: 'CRM Manager' },
      { value: 'head_of_sales', label: 'Head of Sales' },
      { value: 'head_of_ret',   label: 'Head of Ret' },
      { value: 'sales_team',    label: 'TL Sales' },
      { value: 'ret_team',      label: 'TL Ret' },
      { value: 'sales_agent',   label: 'Sales Agent' },
      { value: 'ret_agent',     label: 'Ret Agent' },
    ],
    crm_manager: [
      { value: 'head_of_sales', label: 'Head of Sales' },
      { value: 'head_of_ret',   label: 'Head of Ret' },
      { value: 'sales_team',    label: 'TL Sales' },
      { value: 'ret_team',      label: 'TL Ret' },
      { value: 'sales_agent',   label: 'Sales Agent' },
      { value: 'ret_agent',     label: 'Ret Agent' },
    ],
  };
  const canChangeUserRole = (role) => (CHANGEABLE_BY[me.role] || []).includes(role);
  const assignableRoles = ASSIGNABLE_ROLES[me.role] || [];

  // ── Theme ─────────────────────────────────────────────────────────────────
  const bg          = isDark ? '#060a14' : '#f4f6fb';
  const card        = isDark ? '#0c1220' : '#ffffff';
  const cardBorder  = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)';
  const textPrimary   = isDark ? '#f0f4ff' : '#111827';
  const textSecondary = isDark ? '#6b7fa8' : '#6b7280';
  const teamBg     = isDark ? '#0a0f1e' : '#f9fafb';
  const agentBg    = isDark ? '#111827' : '#ffffff';
  const agentHover = isDark ? '#1a2035' : '#f3f4f6';

  // ── Data loading ──────────────────────────────────────────────────────────
  useEffect(() => {
    logActivity('view', 'hierarchy', null, 'Viewed Hierarchy');
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [oR, dR, tR, uR] = await Promise.all([
        isOwner ? api.get('/offices') : Promise.resolve({ data: [] }),
        api.get('/desks'),
        api.get('/teams'),
        api.get('/users', { params: { limit: 500 } }),
      ]);
      setOffices(oR.data || []);
      setDesks(dR.data || []);
      setTeams(tR.data || []);
      const loadedUsers = [...(uR.data?.users || [])];
      for (let page = 2; page <= (uR.data?.totalPages || 1); page++) {
        const response = await api.get('/users', { params: { limit: 500, page } });
        loadedUsers.push(...(response.data?.users || []));
      }
      setUsers(loadedUsers);
      // The users API already counts generic, Sales and Retention assignments.
      const counts = {};
      loadedUsers.forEach(user => {
        counts[user.id] = user.clientsActive || 0;
      });
      setClientCounts(counts);
    } catch { toast.error(t('hierarchy.failedLoad')); }
    finally { setLoading(false); }
  };

  // ── Client count helpers ──────────────────────────────────────────────────
  const agentCC  = (userId) => clientCounts[userId] || 0;
  const teamCC   = (team)   => team.members.reduce((s, u) => s + agentCC(u.id), 0);
  const deskCC   = (desk)   => desk.deskTeams.reduce((s, t) => s + teamCC(t), 0);

  // ── Drag & drop ───────────────────────────────────────────────────────────
  const onDragStart = (e, user) => { setDragging(user); e.dataTransfer.effectAllowed = 'move'; };
  const onDragEnd   = () => { setDragging(null); setDropTarget(null); };
  const onDragOver  = (e, teamId) => { e.preventDefault(); setDropTarget(teamId); };
  const onDragLeave = () => setDropTarget(null);

  const ROLE_REMAP = {
    sales:     { ret_agent: 'sales_agent', ret_team: 'sales_team', sales_agent: 'sales_agent', sales_team: 'sales_team' },
    retention: { sales_agent: 'ret_agent', sales_team: 'ret_team', ret_agent: 'ret_agent',    ret_team: 'ret_team'    },
  };

  const onDrop = async (e, team) => {
    e.preventDefault();
    if (!dragging) return;
    if (dragging.team === team.name && dragging.desk === team.deskName) { setDragging(null); setDropTarget(null); return; }
    const targetDept = (team.department || '').toLowerCase();
    const newRole    = ROLE_REMAP[targetDept]?.[dragging.role] ?? dragging.role;
    try {
      await api.put(`/users/${dragging.id}`, {
        name: dragging.name, email: dragging.email,
        role: newRole, department: targetDept || dragging.department,
        team: team.name, desk: team.deskName, office: team.officeName,
      });
      toast.success(`${dragging.name} → ${team.name}`);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || t('hierarchy.moveFailed')); }
    finally { setDragging(null); setDropTarget(null); }
  };

  // ── Role change ───────────────────────────────────────────────────────────
  const changeRole = async (user, newRole) => {
    if (newRole === user.role) { setEditingRole(null); return; }
    try {
      await api.put(`/users/${user.id}`, {
        name: user.name, email: user.email, role: newRole,
        department: user.department, team: user.team, desk: user.desk, office: user.office,
      });
      toast.success(`${user.name}: ${ROLE_LABEL[newRole] || newRole}`);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || t('hierarchy.failed')); }
    finally { setEditingRole(null); }
  };

  // ── RoleSelector ──────────────────────────────────────────────────────────
  const RoleSelector = ({ user, color }) => {
    const clr = color || textSecondary;
    if (!canChangeUserRole(user.role))
      return <span style={{ fontSize: 10, color: clr }}>{ROLE_LABEL[user.role] || user.role}</span>;
    if (editingRole === user.id)
      return (
        <select autoFocus value={user.role}
          onChange={e => changeRole(user, e.target.value)}
          onBlur={() => setEditingRole(null)}
          onClick={e => e.stopPropagation()}
          style={{ fontSize: 10, border: '1px solid #8b5cf6', borderRadius: 4, padding: '1px 4px',
            background: isDark ? '#1a1f3a' : '#fff', color: '#8b5cf6', outline: 'none', cursor: 'pointer' }}>
          {assignableRoles.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
      );
    return (
      <span onClick={e => { e.stopPropagation(); setEditingRole(user.id); }}
        title={t('hierarchy.clickChangeRole')}
        style={{ fontSize: 10, color: clr, cursor: 'pointer', borderBottom: `1px dashed ${isDark ? '#3d4f6b' : '#c4c9d4'}` }}>
        {ROLE_LABEL[user.role] || user.role}
      </span>
    );
  };

  // ── Data tree builders ────────────────────────────────────────────────────
  const belongsToTeam = (user, team) => user.team === team.name
    && user.office === team.officeName && user.desk === team.deskName
    && (user.department || (['sales_team', 'sales_agent'].includes(user.role) ? 'sales'
      : ['ret_team', 'ret_agent'].includes(user.role) ? 'retention' : null)) === team.department;
  const buildTeam = (t) => ({
    ...t,
    leader:  users.find(u => belongsToTeam(u, t) && LEADER_ROLES.has(u.role)),
    members: users.filter(u => belongsToTeam(u, t)),
  });
  const buildDesk = (d) => ({ ...d, deskTeams: teams.filter(t => t.deskId === d.id).map(buildTeam) });
  const buildLeadership = (officeName) => ({
    managers: users
      .filter(u => ['admin','crm_manager'].includes(u.role) && u.office === officeName)
      .sort((a, b) => (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9)),
    headSales: users.find(u => u.role === 'head_of_sales' && u.office === officeName),
    headRet:   users.find(u => u.role === 'head_of_ret'   && u.office === officeName),
  });
  const globalManagers = users
    .filter(u => u.role === 'owner' || (['admin','crm_manager'].includes(u.role) && !u.office))
    .sort((a, b) => (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9));

  const deskBelongsToOffice = (desk, office) =>
    (desk.officeId != null && desk.officeId === office.id) ||
    (desk.officeName && desk.officeName === office.name);

  const visibleDesks = isOwner ? desks : me.office ? desks.filter(d => d.officeName === me.office) : desks;
  const tree = isOwner
    ? [
        ...offices.map(o => ({
          id: o.id, label: o.name,
          leadership: buildLeadership(o.name),
          desks: desks.filter(d => deskBelongsToOffice(d, o)).map(buildDesk),
        })),
        ...(desks.filter(d => !offices.some(o => deskBelongsToOffice(d, o))).length > 0
          ? [{ id: '_unassigned', label: 'Unassigned', leadership: null, desks: desks.filter(d => !offices.some(o => deskBelongsToOffice(d, o))).map(buildDesk) }]
          : []),
      ]
    : [{ id: '_root', label: null, leadership: me.office ? buildLeadership(me.office) : null, desks: visibleDesks.map(buildDesk) }];

  const totalAgents = users.filter(u => !HEAD_ROLES.has(u.role) && !['admin','owner','crm_manager'].includes(u.role)).length;

  // ── Small badge ───────────────────────────────────────────────────────────
  const Badge = ({ n, icon: Icon, color }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3,
      fontSize: 10, fontWeight: 600, color,
      background: color + '18', border: `1px solid ${color}30`,
      padding: '1px 6px', borderRadius: 10, flexShrink: 0 }}>
      {Icon && <Icon size={9} />}{n}
    </span>
  );

  // ── Team card renderer ────────────────────────────────────────────────────
  const renderTeam = (team, accentColor) => {
    const tId    = `t_${team.id}`;
    const isColl = isCol(tId);
    const isTarget = dropTarget === team.id;
    const agents   = team.members.filter(u => !LEADER_ROLES.has(u.role));
    const clients  = teamCC(team);

    return (
      <div
        onDragOver={e => onDragOver(e, team.id)}
        onDragLeave={onDragLeave}
        onDrop={e => onDrop(e, team)}
        style={{
          borderRadius: 8, overflow: 'hidden',
          border: isTarget ? `2px solid #8b5cf6` : `1px solid ${cardBorder}`,
          background: isTarget ? (isDark ? 'rgba(139,92,246,0.1)' : 'rgba(139,92,246,0.05)') : teamBg,
          boxShadow: isTarget ? '0 0 0 3px rgba(139,92,246,0.2)' : 'none',
          transition: 'all 0.15s',
        }}
      >
        {/* Team header — clickable to collapse */}
        <div onClick={() => toggle(tId)}
          style={{
            padding: '5px 10px', cursor: 'pointer',
            background: isDark ? accentColor + '14' : accentColor + '0d',
            borderBottom: isColl ? 'none' : `1px solid ${accentColor}25`,
            display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          <Caret id={tId} size={10} color={accentColor} />
          <Users size={10} color={accentColor} />
          <span style={{ fontSize: 11, fontWeight: 700, color: textPrimary, flex: 1 }}>{team.name}</span>
          <Badge n={`${team.members.length} 👤`} color={accentColor} />
          {clients > 0 && <Badge n={`${clients} 👥`} color="#f59e0b" />}
        </div>

        {!isColl && (
          <div style={{ padding: '5px 6px' }}>
            {/* Team lead */}
            {team.leader && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '4px 7px', marginBottom: agents.length > 0 ? 4 : 0, borderRadius: 6,
                background: isDark ? 'rgba(16,185,129,0.08)' : 'rgba(16,185,129,0.05)',
                border: '1px solid rgba(16,185,129,0.2)',
              }}>
                <Star size={9} color="#10b981" fill="#10b981" />
                <Avatar name={team.leader.name} size={20} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#10b981', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {team.leader.name}
                  </div>
                  <RoleSelector user={team.leader} color="#10b981" />
                </div>
                {agentCC(team.leader.id) > 0 && (
                  <span style={{ fontSize: 10, color: '#f59e0b', fontWeight: 600, flexShrink: 0 }}>
                    {agentCC(team.leader.id)} 👥
                  </span>
                )}
              </div>
            )}

            {/* Agents */}
            {agents.map(user => {
              const isDragged = dragging?.id === user.id;
              const cc = agentCC(user.id);
              return (
                <div key={user.id}
                  draggable
                  onDragStart={e => onDragStart(e, user)}
                  onDragEnd={onDragEnd}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '4px 7px', borderRadius: 6, marginBottom: 3,
                    background: agentBg, border: `1px solid ${cardBorder}`,
                    cursor: isDragged ? 'grabbing' : 'grab',
                    opacity: isDragged ? 0.3 : 1,
                    transition: 'all 0.12s', userSelect: 'none',
                  }}
                  onMouseEnter={e => { if (!isDragged) e.currentTarget.style.background = agentHover; }}
                  onMouseLeave={e => { e.currentTarget.style.background = agentBg; }}
                >
                  <GripVertical size={10} color={isDark ? '#2d3a55' : '#d1d5db'} style={{ flexShrink: 0 }} />
                  <Avatar name={user.name} size={20} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {user.name}
                    </div>
                    <RoleSelector user={user} />
                  </div>
                  {cc > 0 && (
                    <span style={{ fontSize: 10, fontWeight: 600, color: '#f59e0b', flexShrink: 0, marginLeft: 2 }}>
                      {cc} 👥
                    </span>
                  )}
                </div>
              );
            })}

            {/* Drop zone */}
            {(agents.length === 0 || dragging) && (
              <div style={{
                border: `2px dashed ${isTarget ? '#8b5cf6' : (isDark ? '#1e2a3d' : '#e5e7eb')}`,
                borderRadius: 6, padding: '6px 0', textAlign: 'center',
                fontSize: 11, color: isTarget ? '#8b5cf6' : textSecondary,
                opacity: isTarget ? 1 : 0.4,
                display: dragging || agents.length === 0 ? 'block' : 'none',
              }}>
                {isTarget ? t('hierarchy.dropHere') : t('hierarchy.empty')}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ── Dept branch renderer ──────────────────────────────────────────────────
  const renderDeptBranch = ({ dept, accentColor, head, allDesks, officeId }) => {
    const dId    = `dept_${officeId}_${dept}`;
    const isColl = isCol(dId);
    const deptDesks = allDesks
      .map(d => ({ ...d, deskTeams: d.deskTeams.filter(t => t.department === dept) }))
      .filter(d => d.deskTeams.length > 0);

    return (
      <div>
        {/* Dept header — clickable */}
        <div onClick={() => toggle(dId)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 14px', borderRadius: 10, cursor: 'pointer',
            background: isDark ? accentColor + '15' : accentColor + '0d',
            border: `1px solid ${accentColor}38`,
            boxShadow: `0 2px 10px ${accentColor}18`,
          }}
        >
          <Caret id={dId} size={11} color={accentColor} />
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: accentColor, flex: 1 }}>
            {dept === 'sales' ? 'Sales' : 'Retention'}
          </span>
          {head ? (
            <div onClick={e => e.stopPropagation()} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '2px 8px 2px 4px', borderRadius: 14,
              background: isDark ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.75)',
              border: `1px solid ${accentColor}30`,
            }}>
              <Avatar name={head.name} size={18} ring ringColor={accentColor} />
              <Crown size={9} color={accentColor} />
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: accentColor, lineHeight: 1.2 }}>{head.name}</div>
                <RoleSelector user={head} color={accentColor} />
              </div>
            </div>
          ) : (
            <span style={{ fontSize: 10, color: accentColor, opacity: 0.4, fontStyle: 'italic' }}>{t('hierarchy.noHead')}</span>
          )}
        </div>

        {/* Desk branches */}
        {!isColl && deptDesks.length > 0 && (
          <Branch items={deptDesks} color={accentColor + '55'} gap={6} ml={18} pl={14}
            renderItem={desk => {
              const deskId  = `desk_${desk.id}`;
              const deskCol = isCol(deskId);
              const dc      = deskCC(desk);
              return (
                <div>
                  {/* Desk label — clickable */}
                  <div onClick={() => toggle(deskId)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '3px 11px 3px 8px', borderRadius: 20, cursor: 'pointer', marginBottom: 5,
                      background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                      border: `1px solid ${cardBorder}`,
                    }}
                  >
                    <Caret id={deskId} size={9} color={textSecondary} />
                    <Layers size={10} color={accentColor} style={{ opacity: 0.7 }} />
                    <span style={{ fontSize: 11, fontWeight: 600, color: textSecondary }}>{desk.name}</span>
                    {dc > 0 && (
                      <span style={{ fontSize: 10, fontWeight: 600, color: '#f59e0b',
                        background: '#f59e0b18', border: '1px solid #f59e0b30',
                        padding: '0 5px', borderRadius: 8 }}>
                        {dc} 👥
                      </span>
                    )}
                  </div>

                  {/* Teams */}
                  {!deskCol && (
                    <Branch items={desk.deskTeams} color={accentColor + '35'} gap={5} ml={14} pl={12}
                      renderItem={team => renderTeam(team, accentColor)}
                    />
                  )}
                </div>
              );
            }}
          />
        )}
      </div>
    );
  };

  // ── Managers strip ────────────────────────────────────────────────────────
  const renderManagersStrip = (managers) => (
    <div style={{
      display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6,
      padding: '6px 12px', borderRadius: 10,
      background: isDark ? 'rgba(168,85,247,0.06)' : 'rgba(168,85,247,0.05)',
      border: `1px solid rgba(168,85,247,0.2)`,
    }}>
      {managers.map(m => (
        <div key={m.id} style={{
          display: 'flex', alignItems: 'center', gap: 5,
          padding: '2px 8px 2px 4px', borderRadius: 16,
          background: isDark ? 'rgba(168,85,247,0.14)' : 'rgba(168,85,247,0.08)',
          border: '1px solid rgba(168,85,247,0.3)',
        }}>
          <Avatar name={m.name} size={20} ring ringColor="#a855f7" />
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#a855f7' }}>{m.name}</div>
            <RoleSelector user={m} color={textSecondary} />
          </div>
        </div>
      ))}
    </div>
  );

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: textSecondary, fontSize: 14 }}>
      {t('hierarchy.loading')}
    </div>
  );

  return (
    <div style={{ padding: 24, minHeight: '100%', background: bg }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: textPrimary, margin: 0 }}>{t('hierarchy.title')}</h1>
          <p style={{ fontSize: 12, color: textSecondary, marginTop: 4 }}>
            {t('hierarchy.subtitle')}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {isOwner && <StatPill count={offices.length} label={t('hierarchy.offices')} color="#8b5cf6" />}
          <StatPill count={desks.length}  label={t('hierarchy.desks')}  color="#3b82f6" />
          <StatPill count={teams.length}  label={t('hierarchy.teams')}  color="#10b981" />
          <StatPill count={totalAgents}   label={t('hierarchy.agents')} color="#f59e0b" />
          <button onClick={loadData} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px',
            fontSize: 13, borderRadius: 8, border: `1px solid ${cardBorder}`,
            background: card, color: textSecondary, cursor: 'pointer',
          }}>
            <RefreshCw size={13} /> {t('hierarchy.refresh')}
          </button>
        </div>
      </div>

      {/* ── Global strip ── */}
      {globalManagers.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8,
          padding: '10px 16px', marginBottom: 20, borderRadius: 12,
          background: isDark ? 'rgba(168,85,247,0.06)' : 'rgba(168,85,247,0.05)',
          border: `1px solid ${isDark ? 'rgba(168,85,247,0.2)' : 'rgba(168,85,247,0.15)'}`,
        }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#a855f7', marginRight: 4 }}>
            {t('hierarchy.global')}
          </span>
          {globalManagers.map(m => (
            <div key={m.id} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '3px 10px 3px 5px', borderRadius: 20,
              background: isDark ? 'rgba(168,85,247,0.12)' : 'rgba(168,85,247,0.08)',
              border: '1px solid rgba(168,85,247,0.28)',
            }}>
              <Avatar name={m.name} size={22} ring ringColor="#a855f7" />
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#a855f7' }}>{m.name}</div>
                <RoleSelector user={m} color={textSecondary} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Office trees ── */}
      {tree.map(officeNode => {
        const oId        = `o_${officeNode.id}`;
        const hasOffCard = isOwner && officeNode.label;
        const oCollapsed = isCol(oId);

        const branchItems = [
          ...(officeNode.leadership?.managers?.length > 0
            ? [{ id: '_mgr', _type: 'managers', data: officeNode.leadership.managers }]
            : []),
          { id: '_sales', _type: 'dept', dept: 'sales',     accentColor: '#3b82f6', head: officeNode.leadership?.headSales, allDesks: officeNode.desks, officeId: officeNode.id },
          { id: '_ret',   _type: 'dept', dept: 'retention', accentColor: '#06b6d4', head: officeNode.leadership?.headRet,   allDesks: officeNode.desks, officeId: officeNode.id },
        ];
        const renderItem = (item) => item._type === 'managers'
          ? renderManagersStrip(item.data)
          : renderDeptBranch(item);

        return (
          <div key={officeNode.id} style={{ marginBottom: hasOffCard ? 32 : 0 }}>

            {/* Office banner */}
            {hasOffCard && (
              <div onClick={() => toggle(oId)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  padding: '14px 20px', borderRadius: 12, cursor: 'pointer',
                  background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #9333ea 100%)',
                  boxShadow: '0 4px 20px rgba(124,58,237,0.35)',
                  marginBottom: 2,
                  userSelect: 'none',
                }}
              >
                <div style={{
                  width: 40, height: 40, borderRadius: 11,
                  background: 'rgba(255,255,255,0.18)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <Building2 size={20} color="#fff" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{officeNode.label}</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', marginTop: 2 }}>
                    {officeNode.desks.length} {t('hierarchy.desks')} ·{' '}
                    {officeNode.desks.reduce((s, d) => s + d.deskTeams.length, 0)} {t('hierarchy.teams')} ·{' '}
                    {officeNode.desks.reduce((s, d) => s + d.deskTeams.reduce((ss, t) => ss + t.members.length, 0), 0)} {t('hierarchy.people')}
                  </div>
                </div>
                {oCollapsed
                  ? <ChevronRight size={18} color="rgba(255,255,255,0.6)" />
                  : <ChevronDown  size={18} color="rgba(255,255,255,0.6)" />}
              </div>
            )}

            {/* Tree content */}
            {(!hasOffCard || !oCollapsed) && (
              hasOffCard ? (
                <Branch items={branchItems} color={isDark ? '#1e2a3d' : '#d1d9e6'}
                  gap={10} ml={28} pl={14} renderItem={renderItem} />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {branchItems.map(item => <div key={item.id}>{renderItem(item)}</div>)}
                </div>
              )
            )}

          </div>
        );
      })}

      {!loading && tree.every(o => o.desks.length === 0) && globalManagers.length === 0 && (
        <div style={{ textAlign: 'center', padding: '80px 20px', color: textSecondary }}>
          <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.3 }}>🏗️</div>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6, color: textPrimary }}>{t('hierarchy.emptyTitle')}</div>
          <div style={{ fontSize: 13, opacity: 0.6 }}>{t('hierarchy.emptySubtitle')}</div>
        </div>
      )}
    </div>
  );
}
